import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Full risk disclosure rendered inline in a modal. Mirrors /risk-disclosure. */
export function FullDisclosureModal({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warn" /> Full Risk Disclosure
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
          <Section title="Educational purpose only">
            ForexMind AI is an AI-powered market intelligence and educational
            decision-support platform. All content is provided strictly for
            educational and informational purposes.
          </Section>
          <Section title="No financial advice">
            Nothing on the platform constitutes financial advice, investment
            advice, tax advice, legal advice, or a personal recommendation.
          </Section>
          <Section title="No investment advisory relationship">
            Use of ForexMind AI does not create an advisor–client relationship.
            We do not provide personalized recommendations based on your
            financial situation or objectives.
          </Section>
          <Section title="No brokerage or trade execution">
            ForexMind AI does not execute trades, hold client funds, or operate
            as a broker, dealer, or money manager.
          </Section>
          <Section title="Forex and CFD risk warning">
            Forex and CFD trading involves a high level of risk and may not be
            suitable for all investors. You could lose some or all of your
            invested capital.
          </Section>
          <Section title="Leverage risk">
            Leverage can amplify losses as quickly as gains. Small adverse
            market moves can result in significant losses.
          </Section>
          <Section title="Past performance disclaimer">
            Past performance of any market intelligence is not indicative of
            future results. Historical accuracy figures are aggregated for
            transparency only.
          </Section>
          <Section title="AI limitations">
            AI outputs are model-generated and may be incomplete, delayed, or
            incorrect. They are not infallible and should not be relied upon as
            the sole basis for any trading decision.
          </Section>
          <Section title="Data delay / data error risk">
            Market data may be delayed, throttled, or temporarily incorrect due
            to upstream provider issues. Do not rely on the platform for
            time-critical decisions.
          </Section>
          <Section title="User responsibility">
            You are solely responsible for any trading decisions you make and
            for evaluating the suitability of any setup for your personal
            circumstances.
          </Section>
          <Section title="Jurisdiction restrictions">
            Access may be limited or restricted in certain jurisdictions based
            on local laws and platform policy. You confirm you are legally
            permitted to access the platform from your location.
          </Section>
          <Section title="No guarantee of availability">
            We do not guarantee uninterrupted availability, accuracy, or
            error-free operation of the service.
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-foreground">{title}</h3>
      <p>{children}</p>
    </div>
  );
}
